import { useState, type RefObject } from 'react';
import { Smile } from 'lucide-react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    emojis: [
      '😀','😃','😄','😁','😆','😅','😂','🤣','😊','🙂','😉','😍','😘','😗','😙','😚','🥰','😋','😛','😜','🤪','😝','🤗','🤩','🥳','😎','🤓','🧐','🤔','🤨','😐','😑','😶','🙄','😏','😒','😔','😞','😟','😕','🙁','☹️','😣','😖','😫','😩','🥺','😢','😭','😤','😠','😡','🤯','😳','🥵','🥶','😱','😨','😰','😥','😓','🤥','🤫','🤐','😬','🙃','😴','😪','🤤','😷','🤒','🤕','🤧','🥴','😵','🤠'
    ],
  },
  {
    label: 'Gestures',
    emojis: ['👍','👎','👏','🙌','🙏','👋','🤝','✌️','🤞','🤟','🤘','👌','🤌','🤏','👈','👉','👆','👇','☝️','✋','🤚','🖐️','🖖','💪','🦾','👊','✊','🤛','🤜'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','💌'],
  },
  {
    label: 'Symbols',
    emojis: ['🔥','✨','⭐','🌟','💫','💥','💯','✅','☑️','✔️','❌','⛔','❗','❓','💡','🎉','🎊','🎁','🏆','🥇','🥈','🥉','🎯','🚀','📈','📉','📌','📎','🔔','🔕','🔒','🔓','🕒','⏰','📅','📝','📚','✉️','💬','🗯️','💭','🌍','☀️','🌙','☁️','⚡','🌈','☕','🍀'],
  },
];

interface EmojiPickerButtonProps {
  textareaRef: RefObject<HTMLTextAreaElement>;
  setValue: (updater: (prev: string) => string) => void;
  className?: string;
  size?: 'sm' | 'icon';
}

/**
 * Emoji picker for chat composers. Inserts the chosen emoji at the
 * current cursor position in the textarea (or appends if no focus).
 */
export function EmojiPickerButton({
  textareaRef,
  setValue,
  className,
  size = 'icon',
}: EmojiPickerButtonProps) {
  const [open, setOpen] = useState(false);

  const insert = (emoji: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setValue((prev) => prev + emoji);
      return;
    }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    setValue((prev) => prev.slice(0, start) + emoji + prev.slice(end));
    // Restore caret position after React updates the value
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      const pos = start + emoji.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={size}
          className={cn(
            'text-muted-foreground hover:text-foreground shrink-0',
            size === 'icon' ? 'h-9 w-9' : 'h-7 px-2 gap-1',
            className
          )}
          aria-label="Insert emoji"
        >
          <Smile className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverPrimitive.Content
        side="top"
        align="end"
        sideOffset={6}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="z-[200] w-[300px] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      >
        <div className="max-h-[280px] overflow-y-auto space-y-3 pr-1">
          {EMOJI_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 px-1">
                {group.label}
              </div>
              <div className="grid grid-cols-8 gap-0.5">
                {group.emojis.map((emoji, i) => (
                  <button
                    key={`${group.label}-${i}`}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      insert(emoji);
                    }}
                    className="text-lg leading-none rounded-md p-1.5 hover:bg-muted transition-colors"
                    aria-label={`Insert ${emoji}`}
                  >
                    <span aria-hidden>{emoji}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverPrimitive.Content>
    </Popover>
  );
}